// Industries → "The Connected Industry"
// Five sector clusters that together represent the UK highways sector.
// Each sector has a colour, headline, long-form copy, sub-industry chips
// and a hover motif.

export const SECTOR_NETWORK = [
  {
    key: "design-engineering",
    iconName: "Compass",
    label: "Design & Engineering",
    tagline: "Where every road begins.",
    color: "#38BDF8", // sky-400
    colorClass: "text-sky-400",
    ringClass: "ring-sky-400/40",
    gradient: "from-sky-500/25 via-sky-500/5 to-transparent",
    dotClass: "bg-sky-400",
    hoverMotif: "blueprint",
    image: "/images/case-studies/rc-williams/hero.jpg",
    headline:
      "The specialists who imagine, model and engineer the UK's future road network.",
    body: [
      "Before a single lane closes or a section of Type 1 goes down, thousands of engineers, surveyors, ecologists and consultants have already been at work. Design and engineering is the discipline that turns a strategic outline into a build-ready programme, translating traffic modelling, topography, ground conditions and community impact into drawings a contractor can price.",
      "Civil engineers, bridge engineers, geotechnical specialists, transport planners and pavement designers work alongside laboratory testing houses and independent consultancies to make sure highways are safe, resilient and buildable. Their decisions determine how long an asset will last, how much it will cost to maintain and whether it can absorb the demands of heavier vehicles and a changing climate.",
      "Small errors at this stage compound into major costs later, which is why our editorial team spends so much time telling the story of the specialists who get it right the first time.",
    ],
    industries: [
      { label: "Civil Engineering", slug: "civil-engineering" },
      { label: "Bridge Engineering", slug: "bridge-engineering" },
      { label: "Structures", slug: "structures" },
      { label: "Geotechnical Engineering", slug: "geotechnical-engineering" },
      { label: "Pavement Engineering", slug: "pavement-engineering" },
      { label: "Surveying", slug: "surveying" },
      { label: "Transport Planning", slug: "transport-planning" },
      { label: "Consultancy", slug: "consultancy" },
      { label: "Ecology", slug: "ecology" },
      { label: "Laboratory Testing", slug: "laboratory-testing" },
      { label: "Testing & Inspection", slug: "testing-and-inspection" },
    ],
  },
  {
    key: "construction-infrastructure",
    iconName: "HardHat",
    label: "Construction & Infrastructure",
    tagline: "The teams that build Britain.",
    color: "#F97316", // orange-500
    colorClass: "text-orange-400",
    ringClass: "ring-orange-400/40",
    gradient: "from-orange-500/25 via-orange-500/5 to-transparent",
    dotClass: "bg-orange-400",
    hoverMotif: "dust",
    image: "/images/case-studies/hds-kier/hero.jpg",
    headline:
      "The contractors, material suppliers and specialists who turn drawings into road.",
    body: [
      "Construction is the moment the industry becomes visible. Earthworks begin, structures rise, pavements are laid, drainage is dug in and utilities are diverted around them. Everything that follows on the network, from bus lanes to smart motorways, depends on this phase being delivered well.",
      "The businesses inside this cluster carry an enormous responsibility. Asphalt producers, concrete repair specialists, drainage contractors, structures teams and utilities providers all coordinate against tight possession windows, often overnight and often in difficult ground. Getting materials, plant and people to the right place at the right time is a genuine operational art.",
      "Great construction businesses know that a strong industry reputation is worth more than any framework score. Our platform exists to help them build that reputation across the audience that already respects it.",
    ],
    industries: [
      { label: "Surfacing", slug: "surfacing-contractors" },
      { label: "Asphalt", slug: "asphalt" },
      { label: "Concrete Repairs", slug: "concrete-repairs" },
      { label: "Bridges & Structures", slug: "bridges-and-structures" },
      { label: "Construction Materials", slug: "construction-materials" },
      { label: "Drainage", slug: "drainage" },
      { label: "Recycling", slug: "recycling" },
      { label: "Utilities", slug: "utilities" },
    ],
  },
  {
    key: "maintenance-operations",
    iconName: "TrafficCone",
    label: "Maintenance & Operations",
    tagline: "Keeping the network moving.",
    color: "#FBBF24", // amber-400
    colorClass: "text-amber-400",
    ringClass: "ring-amber-400/40",
    gradient: "from-amber-500/25 via-amber-500/5 to-transparent",
    dotClass: "bg-amber-400",
    hoverMotif: "roadmark",
    image: "/images/clients/blakedale.jpg",
    headline:
      "The people on the network overnight so the country can travel by day.",
    body: [
      "Once a road opens, the real work begins. Highway maintenance, temporary traffic management, road marking, winter service, fencing, tree care and line marking crews keep the UK's strategic and local road networks safe, legible and available around the clock.",
      "This cluster is the operational heartbeat of the industry. It is highly regulated, safety-critical and hugely dependent on skilled labour, well maintained plant and rigorous planning. When a lane needs closing on the M25 at 2am, dozens of businesses will already have coordinated to make that lane closure safe.",
      "The businesses in this space often carry the strongest project stories. We turn those stories into video, photography and editorial that puts their expertise in front of contractors, National Highways and every local authority that procures the same work.",
    ],
    industries: [
      { label: "Highway Maintenance", slug: "highway-maintenance" },
      {
        label: "Traffic Management",
        slug: "traffic-management-contractors",
      },
      { label: "Temporary Traffic Management", slug: "temporary-traffic-management" },
      { label: "Road Marking", slug: "road-marking" },
      { label: "Line Marking", slug: "line-marking" },
      { label: "Winter Maintenance", slug: "winter-maintenance" },
      { label: "Fencing", slug: "fencing" },
      { label: "Arboriculture", slug: "arboriculture" },
    ],
  },
  {
    key: "products-plant-technology",
    iconName: "Cpu",
    label: "Products, Plant & Technology",
    tagline: "The kit and code behind every project.",
    color: "#A78BFA", // violet-400
    colorClass: "text-violet-400",
    ringClass: "ring-violet-400/40",
    gradient: "from-violet-500/25 via-violet-500/5 to-transparent",
    dotClass: "bg-violet-400",
    hoverMotif: "circuit",
    image: "/images/clients/swarco.jpg",
    headline:
      "The manufacturers, technology firms and plant specialists that supply the sector.",
    body: [
      "This is the cluster that supplies everything a construction or maintenance business needs to actually do the job. Vehicle restraint systems, traffic signals, ITS platforms, VMS boards, road studs, lighting columns, sign faces, drainage components and specialist plant all originate here.",
      "It is also where the industry's innovation curve is at its steepest. Data-driven asset management, smart motorway technology, sensor networks, electric plant and cloud-based site software are transforming productivity and safety in a way that engineering alone could not.",
      "Our audience actively seeks out the businesses inside this cluster, because these are the companies whose product decisions ripple through the entire supply chain. When a specifier searches for a product on HighwaysIndustry.com, our job is to make sure the right manufacturer is the one they find.",
    ],
    industries: [
      { label: "Barriers & Vehicle Restraint Systems", slug: "barriers-and-vehicle-restraint-systems" },
      { label: "Traffic Signals", slug: "traffic-signals" },
      { label: "Intelligent Transport Systems", slug: "intelligent-transport-systems" },
      { label: "Highway Technology", slug: "highway-technology" },
      { label: "Applications & Software", slug: "applications-and-software" },
      { label: "Manufacturers", slug: "manufacturers" },
      { label: "Plant Hire", slug: "plant-hire" },
      { label: "Vehicle Hire", slug: "vehicle-hire" },
      { label: "Equipment Hire", slug: "equipment-hire" },
      { label: "Street Lighting", slug: "street-lighting" },
      { label: "Lighting", slug: "lighting" },
      { label: "Signage", slug: "signage" },
      { label: "Road Safety", slug: "road-safety" },
    ],
  },
  {
    key: "professional-services",
    iconName: "Handshake",
    label: "Professional Services",
    tagline: "The people who keep the industry professional.",
    color: "#34D399", // emerald-400
    colorClass: "text-emerald-400",
    ringClass: "ring-emerald-400/40",
    gradient: "from-emerald-500/25 via-emerald-500/5 to-transparent",
    dotClass: "bg-emerald-400",
    hoverMotif: "nodes",
    image: "/images/clients/wdm.jpg",
    headline:
      "Training providers, recruiters, event organisers and the local authorities that procure the work.",
    body: [
      "Behind every successful project is a professional infrastructure that rarely gets the attention it deserves. Training providers develop the workforce. Recruiters place the right people into the right teams. Health and safety specialists set the standards that keep sites incident-free. Events bring the community together. And local authorities create the demand that keeps every other cluster busy.",
      "This is also the network layer of the industry. It is where relationships are built, contracts are formed and reputations are made. Businesses that invest in visibility inside this cluster tend to win more work, hire better people and open doors that closed ones never would have.",
      "We tell these stories deliberately. Because when the sector talks about who is doing highways well, we want your name to already be part of that conversation.",
    ],
    industries: [
      { label: "Training", slug: "training" },
      { label: "Recruitment", slug: "recruitment" },
      { label: "Events", slug: "events" },
      { label: "Health & Safety", slug: "health-and-safety" },
      { label: "Local Authorities", slug: "local-authorities" },
      { label: "Professional Services", slug: "professional-services" },
    ],
  },
];

// Legacy exports for anywhere still importing these names.
export const FLAGSHIP_INDUSTRIES = SECTOR_NETWORK.map((s) => ({
  slug: s.key,
  label: s.label,
  image: s.image,
  description: s.headline,
}));

export const ALL_INDUSTRIES = SECTOR_NETWORK.flatMap((s) => s.industries);
